import { resolveDashboardCarbonFields } from './carbonAnalyticsFields';

export interface CarbonStatsPayload {
  currentScore?: number;
  totalCO2Emissions?: number;
  esgScopes?: {
    scope1?: { total?: number; percentage?: number; breakdown?: Record<string, number> };
    scope2?: { total?: number; percentage?: number; breakdown?: Record<string, number> };
    scope3?: { total?: number; percentage?: number; breakdown?: Record<string, number> };
  };
  assessmentPeriod?: { startDate?: string; endDate?: string };
  lastAssessment?: string;
  predictions?: Array<{ predictedCO2?: number }>;
  categoryBreakdown?: Record<string, { co2Emissions?: number; co2?: number }>;
  mobileBreakdown?: Record<string, { co2?: number; co2Emissions?: number; count?: number; amount?: number }>;
  recommendations?: unknown[];
}

export interface MSMEData {
  companyName: string;
  gstNumber: string;
  isVerified?: boolean;
  status?: string;
  companyType?: string;
  industry?: string;
  businessDomain?: string;
  business?: {
    numberOfEmployees?: number;
    manufacturingWorkflow?: {
      units?: Array<{ name?: string; products?: string[] | string }>;
    };
  };
  manufacturingProfile?: {
    exportActivity?: boolean;
    supplyChainType?: string;
    primaryEnergySource?: string;
    electricityDiscomId?: string;
    mainFuelsUsed?: string[];
    ghgOrganizationalBoundary?: Record<string, unknown>;
    [key: string]: unknown;
  };
  operations?: {
    ghgOperationalBoundary?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

export interface CarbonSavings {
  totalSavings: number;
  periodSavings: number;
  savingsPercentage: number;
  benchmarkComparison: {
    performance?: string;
  };
  achievements: Array<{ title: string; description: string; level: string }>;
  nextMilestones: Array<{
    title: string;
    description: string;
    currentValue: number;
    targetValue: number;
  }>;
}

export interface AdditionalSavingsMetrics {
  totalCO2Saved?: number;
  costSavings?: number;
  environmentalImpact?: {
    treesEquivalent?: number;
    carsOffRoad?: number;
    energySaved?: number;
  };
}

export interface DocumentCarbonSummary {
  periodType?: 'annual' | 'monthly' | 'weekly' | 'date-wise';
  startDate?: string;
  endDate?: string;
  totals?: {
    transactionCount?: number;
    totalAmount?: number;
    totalCO2Emissions?: number;
    averageEmissionPerTransaction?: number;
  };
}

function unwrapApiPayload<T>(response: unknown): T | null {
  if (!response || typeof response !== 'object') {
    return null;
  }
  const record = response as Record<string, unknown>;
  const nestedData = record.data;
  if (nestedData && typeof nestedData === 'object') {
    return nestedData as T;
  }
  return record as T;
}

export function normalizeMsmeProfileForDashboard(
  raw: Record<string, unknown> | null | undefined
): MSMEData | null {
  if (!raw || typeof raw !== 'object') return null;

  const business = raw.business as Record<string, unknown> | undefined;
  const manufacturingProfile = raw.manufacturingProfile && typeof raw.manufacturingProfile === 'object'
    ? (raw.manufacturingProfile as MSMEData['manufacturingProfile'])
    : undefined;
  const operations = raw.operations && typeof raw.operations === 'object'
    ? (raw.operations as MSMEData['operations'])
    : undefined;
  const manufacturingWorkflow = business?.manufacturingWorkflow && typeof business.manufacturingWorkflow === 'object'
    ? (business.manufacturingWorkflow as NonNullable<MSMEData['business']>['manufacturingWorkflow'])
    : undefined;

  return {
    companyName: String(raw.companyName ?? ''),
    gstNumber: String(raw.gstNumber ?? ''),
    isVerified: Boolean(raw.isVerified),
    status: String(raw.status ?? ''),
    companyType: String(raw.companyType ?? business?.companyType ?? ''),
    industry: String(raw.industry ?? business?.industry ?? ''),
    businessDomain: raw.businessDomain != null ? String(raw.businessDomain) : undefined,
    // Keep My company completion fields so Home checklist matches persisted profile.
    manufacturingProfile,
    operations,
    business: {
      numberOfEmployees: Number(business?.numberOfEmployees ?? raw.numberOfEmployees ?? 0) || undefined,
      manufacturingWorkflow,
    },
  };
}

function parseDocumentPeriodType(value: unknown): DocumentCarbonSummary['periodType'] {
  if (value === 'annual' || value === 'monthly' || value === 'weekly' || value === 'date-wise') {
    return value;
  }
  return undefined;
}

function parseOptionalNumber(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function buildDashboardBundle(input: {
  profileResponse: unknown;
  statsResponse: unknown;
  savingsResponse: unknown;
  dashboardResponse: unknown;
}) {
  const profilePayload = unwrapApiPayload<Record<string, unknown>>(input.profileResponse);
  const statsPayload = unwrapApiPayload<Record<string, unknown>>(input.statsResponse);
  const savingsPayload = unwrapApiPayload<Record<string, unknown>>(input.savingsResponse);
  const dashboardPayload = unwrapApiPayload<Record<string, unknown>>(input.dashboardResponse);
  const dashboardKeyData = (dashboardPayload?.keyData || {}) as Record<string, unknown>;
  const statsCarbon = (statsPayload?.carbon as CarbonStatsPayload | undefined) || undefined;
  const dashboardCategoryBreakdown =
    (dashboardPayload?.categoryBreakdown as CarbonStatsPayload['categoryBreakdown']) || null;
  const resolvedCarbon = resolveDashboardCarbonFields(statsCarbon, dashboardCategoryBreakdown);
  const mergedCarbon: CarbonStatsPayload = {
    ...(statsCarbon || {}),
    currentScore:
      Number(statsCarbon?.currentScore)
      || Number(dashboardPayload?.currentScore)
      || 0,
    totalCO2Emissions:
      Number(statsCarbon?.totalCO2Emissions)
      || Number(dashboardPayload?.currentMonthEmissions)
      || Number(dashboardKeyData.totalEmissions)
      || 0,
    categoryBreakdown: resolvedCarbon.categoryBreakdown || undefined,
    esgScopes: resolvedCarbon.esgScopes
      || (dashboardPayload?.esgScopes as CarbonStatsPayload['esgScopes'])
      || statsCarbon?.esgScopes,
    mobileBreakdown: statsCarbon?.mobileBreakdown,
    recommendations:
      statsCarbon?.recommendations
      || (dashboardPayload && Array.isArray(dashboardPayload.topRecommendations)
        ? dashboardPayload.topRecommendations
        : []),
    predictions: statsCarbon?.predictions || [],
    assessmentPeriod: statsCarbon?.assessmentPeriod,
    lastAssessment: statsCarbon?.lastAssessment,
  };

  const mergedStats = statsPayload || dashboardPayload
    ? {
        ...(statsPayload || {}),
        carbon: mergedCarbon,
        topRecommendations:
          statsPayload?.topRecommendations
          || dashboardPayload?.topRecommendations
          || [],
        transactions: statsPayload?.transactions || {
          total: Math.max(
            Number(dashboardKeyData.totalTransactions) || 0,
            Number(dashboardPayload?.totalTransactions) || 0
          ),
        },
        carbonCredits: statsPayload?.carbonCredits || dashboardPayload?.greenCredits || null,
      }
    : null;

  const bulk = statsPayload?.documentBulkUpload as Record<string, unknown> | undefined;
  let documentCarbonSummary: DocumentCarbonSummary | null = null;
  if (bulk) {
    const txCount = Number(bulk.totalTransactions) || 0;
    const co2 = Number(bulk.totalCO2Emissions) || 0;
    documentCarbonSummary = {
      periodType: parseDocumentPeriodType(bulk.periodType),
      totals: {
        transactionCount: txCount,
        totalAmount: parseOptionalNumber(bulk.totalAmount),
        totalCO2Emissions: co2,
        averageEmissionPerTransaction: txCount > 0 ? co2 / txCount : 0,
      },
    };
  }

  return {
    msmeData: normalizeMsmeProfileForDashboard(profilePayload),
    statsData: mergedStats as Record<string, any> | null,
    carbonSavings: (savingsPayload?.savings as CarbonSavings) || null,
    additionalSavingsMetrics: (savingsPayload?.additionalMetrics as AdditionalSavingsMetrics) || null,
    documentCarbonSummary,
  };
}
